import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { combineLatest, firstValueFrom, Observable } from 'rxjs';
import { map, shareReplay, take, tap, withLatestFrom } from 'rxjs/operators';
import { compareTap, delayAtLeast } from '@dg/shared-rxjs';
import {
  AuthService,
  A11yService,
  CareerPathNamingService,
  ScrollService,
  LDFlagsService,
} from '@dg/shared-services';
import { OrgService } from '@app/orgs/services/org.service';
import {
  OrganizationModel,
  OrganizationModelIdentifier,
} from '@app/orgs/services/orgs.model';
import { SearchService } from '@app/search/services/search.service';
import { TranslateService } from '@ngx-translate/core';
import { AuthUser } from '@app/account/account-api.model';
import { SearchFlagsService } from '@app/search/services/search-flags.service';
import { Title } from '@angular/platform-browser';
import {
  SearchOrigin,
  SearchResultView,
  SearchSubmitMethod,
  SearchTrackerService,
} from '@app/search/services/search-tracker.service';
import { SearchFiltersStateService } from '@app/search/components/search-filters/search-filters-state.service';
import {
  searchExecutedEvent,
  searchViewChangedEvent,
  loadMoreEvent,
  addExternalCatalog,
  buildComputed,
  ComputeLookups,
  getOrgId,
  mergeOverrides,
  stateToLearningOptions,
  atFirstPage,
  appliedFacetsToUrlFilters,
  mayLoadEndorsedContent,
  showEndorsedContent,
} from './utils';
import { SearchStore } from './search.store';
import { SearchDataService } from './search.data-service';
import {
  SearchState,
  SearchFilter,
  SearchViewModel,
  SearchSort,
  SearchComputedState,
  SearchAPI,
  SearchLearningResponse,
  SearchChangeTrigger,
  OpportunityResult,
  SkillResult,
  PeopleResult,
  GroupResult,
  RelatedSkillResult,
  FeaturedItemResult,
} from './models/search.model';
import { SearchRelatedFacade } from './search-related.facade';
import { SearchEndorsedFacade } from './search-endorsed.facade';
import { getIsError, ReactiveListVM } from '@dg/shared-rsm';
import { capitalizeFirstLetter, getDeepCopy } from '@app/shared/utils';
import { OpportunityPermissionsService } from '@app/opportunities/services/opportunity-permissions.service';
import {
  WindowLayoutService,
  EventBus,
  Unsubscribe,
  SearchEvents,
} from '@app/shared/services';
import { LearningResourceViewModel } from '@app/inputs/models/learning-resource.view-model';
import { DetailsModalService } from '@app/shared/services/content/details-modal.service';
import { SearchFiltersService } from '@app/search/services';

/**
 * Load Learnings and cache results for similar future calls.
 * Reactive Architecture:
 *       UI <-> ViewModel <-> Facade <-> Store
 *                              | --> DataService
 */
@Injectable()
export class SearchFacade implements OnDestroy {
  // Viewmodel streams for search results
  public readonly opportunities$: Observable<ReactiveListVM<OpportunityResult>>;
  public readonly skills$: Observable<ReactiveListVM<SkillResult>>;
  public readonly people$: Observable<ReactiveListVM<PeopleResult>>;
  public readonly groups$: Observable<ReactiveListVM<GroupResult>>;
  public readonly relatedSkills$: Observable<
    ReactiveListVM<RelatedSkillResult>
  >;
  public readonly endorsedSearch$: Observable<
    ReactiveListVM<LearningResourceViewModel>
  >;

  public readonly featuredItems$: Observable<
    ReactiveListVM<FeaturedItemResult>
  >;

  public readonly showEndorsedContent$: Observable<boolean>;
  public readonly mayLoadEndorsedContent$: Observable<boolean>;

  public readonly hasError$: Observable<boolean>;
  public readonly vm$: Observable<SearchViewModel>;
  public readonly showSearchBar$: Observable<boolean>;

  private careerPathNamesLoaded = false;
  private detachEventBus: Unsubscribe;

  constructor(
    private store: SearchStore,
    private relatedSearch: SearchRelatedFacade,
    private endorsedSearch: SearchEndorsedFacade,
    private searchDataService: SearchDataService,
    private searchService: SearchService,
    private flagsService: SearchFlagsService,
    private trackerService: SearchTrackerService,
    private searchFiltersStateService: SearchFiltersStateService,
    private translateService: TranslateService,
    private orgService: OrgService,
    private authService: AuthService,
    private titleService: Title,
    private a11yService: A11yService,
    private careerPathNamingService: CareerPathNamingService,
    private scrollService: ScrollService,
    private windowLayoutService: WindowLayoutService,
    private opportunityPermissionsService: OpportunityPermissionsService,
    private detailsModalService: DetailsModalService,
    private searchFiltersService: SearchFiltersService,
    private eventBus: EventBus,
    private ldFlagsService: LDFlagsService,
    private searchTrackerService: SearchTrackerService
  ) {
    this.searchFiltersStateService.setExtractLanguageFilter(true);

    const authUser$ = this.authService.authUser$;
    const organizations$ = this.orgService.getUserOrganizations().pipe(
      withLatestFrom(authUser$),
      map(([orgs, authUser]) => {
        const srv = this.searchService;
        const getCatalogs = srv.getCatalogs.bind(srv);
        const showMarketplaceCat = this.ldFlagsService.showMarketplace;
        return addExternalCatalog(
          orgs,
          authUser,
          showMarketplaceCat,
          getCatalogs
        );
      })
    );
    const breakpoints$ = this.windowLayoutService.registerMediaListener(
      [
        this.windowLayoutService.dgBreakpoints.BabyBear,
        this.windowLayoutService.dgBreakpoints.TeenieBear,
      ],
      false
    );

    const state$ = this.store.state$.pipe(
      // Track changes by comparing previous/current version of state
      compareTap(this.trackAnalytics.bind(this)),
      // Show learning resource modal
      compareTap(this.viewLearningDetailsModal.bind(this))
    );

    this.vm$ = combineLatest([
      state$,
      authUser$,
      organizations$,
      breakpoints$,
    ]).pipe(
      map(this.addComputedProperties.bind(this)),
      tap(this.handleSideEffects.bind(this)),
      map(this.addViewModelAPI.bind(this)),
      shareReplay<SearchViewModel>({ refCount: false, bufferSize: 1 })
    );

    this.hasError$ = this.vm$.pipe(map(getIsError));

    this.people$ = this.relatedSearch.people.vm$;
    this.groups$ = this.relatedSearch.groups.vm$;
    this.skills$ = this.relatedSearch.skills.vm$;
    this.relatedSkills$ = this.relatedSearch.relatedSkills.vm$;
    this.opportunities$ = this.relatedSearch.opportunities.vm$;
    this.featuredItems$ = this.relatedSearch.featuredItems.vm$;
    this.showSearchBar$ = this.shouldShowSearchBar();
    this.endorsedSearch$ = this.endorsedSearch.endorsedLearnings.vm$;
    this.mayLoadEndorsedContent$ = this.endorsedSearch$.pipe(
      map(mayLoadEndorsedContent)
    );
    this.showEndorsedContent$ = combineLatest([
      this.vm$,
      this.endorsedSearch$,
    ]).pipe(map(showEndorsedContent));

    // If a search is performed in the app header an event will be emitted
    // so we can capture that here and avoid a page reload
    this.detachEventBus = this.eventBus.on(
      SearchEvents.GLOBAL_SEARCH,
      this.applyEventBusAction.bind(this)
    );
  }

  public ngOnDestroy() {
    this.detachEventBus();
  }

  /**
   * Search Learnings
   *
   * Use cache to skip remote load
   * Auto-save to cache; based on specified search keys
   * Also smart prefetch next page...
   */
  public searchLearnings(
    updatedSearch: Partial<SearchState> = {},
    resetStore = true,
    spellcheckSearchTerm = false,
    persistFilter = false
  ): Observable<boolean> {
    return new Observable((subscriber) => {
      const state = this.store.getState(mergeOverrides(updatedSearch));
      const next = (learnings: SearchLearningResponse) => {
        this.store.updateLearnings(learnings, state, resetStore);
        const updatedState = this.store.getState();

        this.prefetchPage(learnings.pagination.currentPage + 1);

        // Load endorsed only results
        const isMarketplaceCat =
          state.orgId === this.searchService.marketplaceCatalog.organizationId;
        if (
          this.flagsService.showPrioritizedEndorsedContent &&
          !this.authService.authUser?.defaultOrgInfo?.settings
            ?.disableEndorsedSearchResultsSection &&
          !isMarketplaceCat
        ) {
          this.endorsedSearch.getEndorsedLearnings(updatedState, true);
        }

        // Load sidebar data etc
        this.relatedSearch.searchWith(
          updatedState.term,
          updatedState.orgId,
          isMarketplaceCat
        );

        subscriber.next(true);
        subscriber.complete();
      };
      const error = (err: HttpErrorResponse) => {
        this.store.handleError(updatedSearch);
        subscriber.error(err);
      };

      if (resetStore) {
        this.store.showSkeleton();
      }

      const learningOptions = stateToLearningOptions(state);
      learningOptions.options.persistFilter = persistFilter;

      this.searchDataService
        .searchLearnings(learningOptions, spellcheckSearchTerm)
        .pipe(this.store.trackLoadStatus(), delayAtLeast(450))
        .subscribe({ next, error });
    });
  }

  // *******************************************************
  // update methods
  // *******************************************************

  /**
   * Update search term
   */
  public async updateTerm(term: string): Promise<boolean> {
    console.log('updateTerm', term);
    const overrides = {
      term,
      isCatalogSearch: true,
      changeTrigger: SearchChangeTrigger.Term,
    };
    const state = atFirstPage({
      ...this.store.getState(),
      ...overrides,
    });

    // Disable ignore preferred language for search term
    this.searchFiltersStateService.setIgnorePreferredLanguage(false);

    const suggestedTermSelected = term === state.suggestedTerm;
    const relatedSkillSelected = await firstValueFrom(
      this.isDisplayedRelatedSkill(term)
    );

    // If user selects suggested term or related skill, skip spellcheck
    const spellcheckSearchTerm =
      !relatedSkillSelected && !suggestedTermSelected;

    // Always reset on term change to clear pagination etc
    const resetStore = true;

    // Tell the back end to cache the filters when the user updates the search term
    const persistFilter = true;

    return firstValueFrom(
      this.searchLearnings(
        state,
        resetStore,
        spellcheckSearchTerm,
        persistFilter
      )
    );
  }

  /**
   * Update sort criteria
   */
  public updateSortBy(sort: SearchSort): Promise<boolean> {
    const overrides = { sort, changeTrigger: SearchChangeTrigger.Sort };
    const state = atFirstPage({
      ...this.store.getState(),
      ...overrides,
    });

    return firstValueFrom(this.searchLearnings(state));
  }

  /**
   * Update selected organization
   */
  public updateOrganization(
    selectedOrg: OrganizationModelIdentifier
  ): Promise<boolean> {
    const orgId = selectedOrg.organizationId || undefined;
    const isExternalCat = selectedOrg.organizationId === undefined || false;
    const isMarketplaceCat =
      selectedOrg.organizationId ===
        this.searchService.marketplaceCatalog.organizationId || false;

    // if switching in or out of the marketplace search, immediately emit that the org has changed and filters are cleared
    // to enable a smooth transition in the UI.
    const updateState = (prevState: SearchState) => {
      if (prevState.isMarketplaceCat !== isMarketplaceCat) {
        return {
          ...prevState,
          isMarketplaceCat,
          isLoading: true,
          appliedFacets: [],
          filters: [],
        };
      }
      return prevState;
    };

    this.store.update(updateState);

    const searchState = {
      ...this.store.getState(),
      orgId,
      isExternalCat,
      isMarketplaceCat,
      changeTrigger: SearchChangeTrigger.Organization,
    };

    return firstValueFrom(this.searchLearnings(atFirstPage(searchState)));
  }

  /**
   * Update current filters
   */
  public updateFilters(uiFilters: SearchFilter[]): Promise<boolean> {
    const appliedFacets = this.searchService.getAppliedFilters(uiFilters);

    // Immediately update filters selection instead of delaying until search results are loaded
    const updateState = (prevState: SearchState) => ({
      ...prevState,
      filters: getDeepCopy(uiFilters),
      transformedSearchFilters: uiFilters,
    });

    this.store.update(updateState);

    // Tell the back end to cache the filters when the user updates the search term
    const persistFilter = true;

    // Term is unchanged, no reason to spellcheck
    const spellcheckSearchTerm = false;

    // Don't reset the store
    const resetStore = false;

    const state = atFirstPage({
      ...this.store.getState(),
      appliedFacets,
      changeTrigger: SearchChangeTrigger.Filters,
    });

    return firstValueFrom(
      this.searchLearnings(
        state,
        resetStore,
        spellcheckSearchTerm,
        persistFilter
      )
    );
  }

  public resetFilters(): Promise<boolean> {
    const state = atFirstPage({
      ...this.store.getState(),
      appliedFacets: [],
      changeTrigger: SearchChangeTrigger.Filters,
    });

    this.searchFiltersStateService.setIgnorePreferredLanguage(false);

    return firstValueFrom(this.searchLearnings(state));
  }

  /**
   * Append more results to endorsed content
   */
  public loadEndorsedContent(): void {
    const state = this.store.getState();
    this.trackLoadMoreEndorsedResults(state);
    this.endorsedSearch.getEndorsedLearnings(state);
  }

  // *******************************************************
  // Paging methods
  // *******************************************************

  /**
   * Show learnings at page #... load if not in cache already
   * Always try to prefetch next page from 'selected'
   */
  public showPage(page = 1): Promise<boolean> {
    const inRange = this.store.pageInRange(page);
    let result = Promise.resolve(false);

    if (inRange) {
      const fromCache = this.store.selectPage(page);

      // Should silently auto load next page?
      if (fromCache) {
        if (!this.store.hasPage(page + 1)) {
          this.prefetchPage(page + 1);
          result = Promise.resolve(true);
        }
      } else {
        const pagination = { currentPage: page };

        result = firstValueFrom(
          this.searchLearnings(
            { pagination, changeTrigger: SearchChangeTrigger.Page },
            false
          )
        );
      }
    }

    return result;
  }

  /**
   * Background prefetch for super-fast page navigation rendering
   * NOTE: do not update status for background prefetching
   */
  private prefetchPage(page: number) {
    if (this.store.pageInRange(page) && !this.store.hasPage(page)) {
      const state = this.store.getState((draft: SearchState) => {
        draft.pagination.currentPage = page;
      });
      const request$ = this.searchDataService.searchLearnings(
        stateToLearningOptions(state)
      );

      request$.subscribe(({ results }: SearchLearningResponse) => {
        this.store.addPage(results, page, false);
      });
    }
  }

  // *******************************************************
  // ViewModel 'injection' methods
  // *******************************************************

  /**
   * Inject the Facade computed properties into view model
   */
  private addComputedProperties([
    state,
    authUser,
    organizations,
    breakpointState,
  ]): [SearchState, AuthUser, OrganizationModel[]] {
    const lookups: ComputeLookups = {
      authUser,
      organizations,
      breakpointState,
      searchService: this.searchService,
      searchFlagsService: this.flagsService,
      translateService: this.translateService,
      opportunityPermissionsService: this.opportunityPermissionsService,
      careerPathNamingService: this.careerPathNamingService,
      searchFiltersService: this.searchFiltersService,
      searchFiltersStateService: this.searchFiltersStateService,
      windowLayoutService: this.windowLayoutService,
      ldFlagsService: this.ldFlagsService,
    };

    // Ensure we have an `orgId` if available
    state.orgId = getOrgId(state, authUser);

    const computed = buildComputed(state, lookups);

    return {
      ...state,
      ...computed,
    };
  }

  /**
   * Inject the Facade API into view model
   */
  private addViewModelAPI(
    state: SearchState & SearchComputedState
  ): SearchState & SearchAPI {
    const api = {
      updateTerm: this.updateTerm.bind(this),
      updateSortBy: this.updateSortBy.bind(this),
      updateFilters: this.updateFilters.bind(this),
      resetFilters: this.resetFilters.bind(this),
      updateOrganization: this.updateOrganization.bind(this),
      loadEndorsedContent: this.loadEndorsedContent.bind(this),
    };

    // Override default showPage with custom functionality to prefetch next page
    state.pagination.showPage = this.showPage.bind(this);

    return {
      ...state,
      ...api,
    };
  }

  // *******************************************************
  // Side Effects
  // *******************************************************

  /**
   * Handle side effects of state changes here
   */
  private handleSideEffects(vm: SearchState & SearchComputedState) {
    this.setPageTitle(vm);
    this.loadCareerPathNames(vm); // Load career pathing names for search filter

    this.scrollService.scrollToPageTop();
    this.a11yService.announceResultsCount(vm.pagination.total);

    const isMarketplaceCat =
      vm.orgId === this.searchService.marketplaceCatalog.organizationId;
    this.setTrackingLocation(isMarketplaceCat);
  }

  /**
   * Update the page title for the current search term
   */
  private setPageTitle({ term, i18n }: SearchState & SearchComputedState) {
    const pageTitle = `${i18n.Core_Search} | Degreed`;
    this.titleService.setTitle(term ? `${term} - ${pageTitle}` : pageTitle);
  }

  /**
   * Make career pathing names available for child components
   */
  private loadCareerPathNames({
    defaultOrgId,
    hasCareerPathing,
  }: SearchState & SearchComputedState) {
    if (hasCareerPathing && !this.careerPathNamesLoaded) {
      this.careerPathNamesLoaded = true;
      this.careerPathNamingService
        .getCareerPathNames(defaultOrgId)
        .pipe(take(1))
        .subscribe();
    }
  }

  // ****************************************************************
  // Search Analytics
  // ****************************************************************

  private trackAnalytics([previous, current]: [SearchState, SearchState]) {
    switch (current.changeTrigger) {
      case SearchChangeTrigger.Filters:
        this.trackFilterChange(previous, current);
        break;
      case SearchChangeTrigger.Sort:
        this.trackSortChange(previous, current);
        break;
      case SearchChangeTrigger.Organization:
        this.trackOrganizationChange(previous, current);
        break;
      case SearchChangeTrigger.Page:
        this.trackPaginationChange(previous, current);
        break;
      case SearchChangeTrigger.Term:
      default:
        this.trackTermChange(previous, current);
    }
  }

  private trackFilterChange(previous: SearchState, current: SearchState) {
    const previousFilters = appliedFacetsToUrlFilters(previous.appliedFacets);
    const currentFilters = appliedFacetsToUrlFilters(current.appliedFacets);
    if (previousFilters !== currentFilters) {
      this.trackSearchResultViewChanged(current);
    }
  }

  private trackSortChange(previous: SearchState, current: SearchState) {
    if (previous.sort !== current.sort) {
      this.trackSearchResultViewChanged(current, {
        linkClicked: SearchSubmitMethod.sortBy,
        sortType: capitalizeFirstLetter(current.sort),
      });
    }
  }

  private trackOrganizationChange(previous: SearchState, current: SearchState) {
    if (previous.orgId !== current.orgId) {
      this.trackSearchExecuted(current);
    }
  }

  private trackPaginationChange(previous: SearchState, current: SearchState) {
    if (previous.pagination.currentPage !== current.pagination.currentPage) {
      this.trackSearchResultViewChanged(current, {
        previousPageNumber: previous.pagination.currentPage,
      });
    }
  }

  private trackTermChange(previous: SearchState, current: SearchState) {
    if (previous.term !== current.term) {
      this.trackSearchExecuted(current);
      this.reportSuggestedTerms(current);
    }
  }

  /**
   * Report changes to search parameters (filter, sort, pagination)
   */
  private trackSearchResultViewChanged(
    state: SearchState,
    trackingProperties?: Partial<SearchResultView>
  ): boolean {
    const totals = this.relatedSearch.totalCounts;
    const event = searchViewChangedEvent(state, totals);

    this.trackerService.searchResultViewChanged({
      ...event,
      ...trackingProperties,
    });
    return true;
  }

  private trackLoadMoreEndorsedResults(state: SearchState) {
    const event = loadMoreEvent(state);
    this.trackerService.loadMoreClicked(event);
  }

  /**
   * Report new search executed  (term or organization change)
   */
  private trackSearchExecuted(state: SearchState) {
    const totals = this.relatedSearch.totalCounts;
    const event = searchExecutedEvent(state, totals);

    const origin = state.isMarketplaceCat
      ? SearchOrigin.marketplace
      : SearchOrigin.searchLearnings;
    firstValueFrom(this.relatedSearch.relatedSkills.vm$).then(({ list }) => {
      const relatedSkills = list.map((skill) => skill.term);
      this.trackerService.searchExecuted({
        ...event,
        relatedSkills,
        origin,
      });
    });
  }

  /**
   * Report auto suggested terms
   */
  private reportSuggestedTerms(state: SearchState) {
    if (state.typoAutoSearched) {
      this.trackerService.searchTypoSuggestionAutoSearched(
        state.previousTerm,
        state.suggestedTerm
      );
    }
  }

  // ****************************************************************
  // Helpers
  // ****************************************************************

  /**
   * Is the provided term one of the currently displayed related skills?
   */
  private isDisplayedRelatedSkill(term: string): Observable<boolean> {
    return this.relatedSkills$.pipe(
      take(1),
      map(({ list }) => list?.map(({ id }) => id).includes(term))
    );
  }

  private shouldShowSearchBar(): Observable<boolean> {
    const {
      isMsTeams,
      dgBreakpoints: { TeenieBear },
    } = this.windowLayoutService;

    return this.windowLayoutService
      .registerMediaListener([TeenieBear], false)
      .pipe(map(({ matches }) => matches && isMsTeams));
  }

  /**
   * Display the learning details modal if `viewLearningId` is provided
   */
  private viewLearningDetailsModal([previous, current]: [
    SearchState,
    SearchState
  ]) {
    // State will emit more than once as pages are cached
    // Only proceed when the `viewLearningId` property is initially set
    const learningIdInitialized =
      current.viewLearningId &&
      previous.viewLearningId !== current.viewLearningId;

    if (!learningIdInitialized) {
      return;
    }

    // Find the learning resource in the current search results
    const learningResourceVM = current.results.find(
      (result) => result.model.resourceId === current.viewLearningId
    );

    // Fail gracefully and alert the developer if the learning resource is not found
    if (!learningResourceVM) {
      console.warn(
        `${current.viewLearningId} does not match any resource IDs in the current search results`
      );
      return;
    }

    const modal = this.detailsModalService.openDetails(learningResourceVM);

    // clear the `viewLearningId` state property when the user closes the modal
    // to prevent it from showing again on page refresh
    firstValueFrom(modal).then(() =>
      this.store.update((state) => (state.viewLearningId = undefined))
    );
  }

  private async applyEventBusAction(params: Partial<SearchState>) {
    const resetStore = true;
    const spellcheckSearchTerm = true;
    const persistFilter = true;

    return await firstValueFrom(
      this.searchLearnings(
        {
          ...params,
          changeTrigger: SearchChangeTrigger.Term,
        },
        resetStore,
        spellcheckSearchTerm,
        persistFilter
      )
    );
  }

  private setTrackingLocation(isMarketplaceCat: boolean): void {
    if (isMarketplaceCat) {
      this.searchTrackerService.setTrackerLocationMarketplace();
    } else {
      this.searchTrackerService.setTrackerLocationSearch();
    }
  }
}
